import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';

const getToken = () => localStorage.getItem('rz_token');

export default function Settings() {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [tab, setTab] = useState('account');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [account, setAccount] = useState({
    name: '', phone: '', address: '', city: '', state: '', zip: '', website: ''
  });

  const [aiSettings, setAiSettings] = useState({
    ai_include_pricing: true,
    default_price_display: 'full_price',
    dealer_type: 'independent'
  });

  const [passwords, setPasswords] = useState({
    current: '', newPass: '', confirm: ''
  });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    const r = await fetch('/api/settings', { headers: { 'Authorization': 'Bearer ' + getToken() } });
    const d = await r.json();
    if (d.dealer) {
      setAccount({
        name: d.dealer.name || '',
        phone: d.dealer.phone || '',
        address: d.dealer.address || '',
        city: d.dealer.city || '',
        state: d.dealer.state || '',
        zip: d.dealer.zip || '',
        website: d.dealer.website || '',
      });
      setAiSettings({
        ai_include_pricing: d.dealer.ai_include_pricing !== false,
        default_price_display: d.dealer.settings?.default_price_display || 'full_price',
        dealer_type: d.dealer.dealer_type || 'independent',
      });
    }
    setLoading(false);
  };

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  const showError = (msg) => { setError(msg); setTimeout(() => setError(''), 4000); };

  const saveAccount = async () => {
    setSaving(true);
    const r = await fetch('/api/settings/account', {
      method: 'PUT',
      headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
      body: JSON.stringify(account)
    });
    const d = await r.json();
    setSaving(false);
    if (d.error) { showError(d.error); return; }
    showSuccess('Account settings saved!');
  };

  const saveAI = async () => {
    setSaving(true);
    const r = await fetch('/api/settings/ai', {
      method: 'PUT',
      headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
      body: JSON.stringify(aiSettings)
    });
    const d = await r.json();
    setSaving(false);
    if (d.error) { showError(d.error); return; }
    showSuccess('AI settings saved!');
  };

  const changePassword = async () => {
    if (passwords.newPass !== passwords.confirm) { showError('New passwords do not match'); return; }
    if (passwords.newPass.length < 8) { showError('Password must be at least 8 characters'); return; }
    setSavingPassword(true);
    const r = await fetch('/api/settings/password', {
      method: 'PUT',
      headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ current_password: passwords.current, new_password: passwords.newPass })
    });
    const d = await r.json();
    setSavingPassword(false);
    if (d.error) { showError(d.error); return; }
    setPasswords({ current: '', newPass: '', confirm: '' });
    showSuccess('Password changed successfully!');
  };

  const tabs = [
    { id: 'account', label: '🏢 Account' },
    { id: 'ai', label: '🤖 AI Settings' },
    { id: 'password', label: '🔒 Password' },
  ];

  if (loading) return <div className="loading">Loading settings...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Dealer <span>Settings</span></h1>
      </div>

      {success && <div style={{ background: '#0a2a0a', border: '1px solid #1a4a1a', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#4caf50', fontSize: 14 }}>✓ {success}</div>}
      {error && <div style={{ background: '#1a0000', border: '1px solid #3a0000', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#ff4444', fontSize: 14 }}>✗ {error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '1px solid #222' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 20px', background: 'transparent', border: 'none',
            borderBottom: tab === t.id ? '2px solid #cc0000' : '2px solid transparent',
            color: tab === t.id ? '#cc0000' : '#888', cursor: 'pointer', fontSize: 14, fontWeight: 600
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'account' && (
        <div style={{ maxWidth: 600 }}>
          <div className="form-section">
            <h3>Dealership Information</h3>
            <div className="form-grid">
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label>Dealership Name</label>
                <input value={account.name} onChange={e => setAccount(a => ({ ...a, name: e.target.value }))} placeholder="Auto Finance Advantage" />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input value={account.phone} onChange={e => setAccount(a => ({ ...a, phone: e.target.value }))} placeholder="(555) 000-0000" />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input value={account.website} onChange={e => setAccount(a => ({ ...a, website: e.target.value }))} placeholder="https://yoursite.com" />
              </div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label>Address</label>
                <input value={account.address} onChange={e => setAccount(a => ({ ...a, address: e.target.value }))} placeholder="123 Main St" />
              </div>
              <div className="form-group">
                <label>City</label>
                <input value={account.city} onChange={e => setAccount(a => ({ ...a, city: e.target.value }))} placeholder="Summerville" />
              </div>
              <div className="form-group">
                <label>State</label>
                <input value={account.state} onChange={e => setAccount(a => ({ ...a, state: e.target.value }))} placeholder="SC" />
              </div>
              <div className="form-group">
                <label>ZIP</label>
                <input value={account.zip} onChange={e => setAccount(a => ({ ...a, zip: e.target.value }))} placeholder="29483" />
              </div>
            </div>
            <button className="btn-red" onClick={saveAccount} disabled={saving}>{saving ? 'Saving...' : 'Save Account Settings'}</button>
          </div>
        </div>
      )}

      {tab === 'ai' && (
        <div style={{ maxWidth: 600 }}>
          <div className="form-section">
            <h3>AI Description Settings</h3>
            <div className="form-group">
              <label>Dealership Type</label>
              <select value={aiSettings.dealer_type} onChange={e => setAiSettings(a => ({ ...a, dealer_type: e.target.value }))}>
                <option value="independent">Independent Used Car</option>
                <option value="bhph">Buy Here Pay Here (BHPH)</option>
                <option value="franchise">Franchise Dealer</option>
                <option value="wholesale">Wholesale / Auction</option>
                <option value="powersports">RV / Powersports</option>
                <option value="commercial">Commercial / Fleet</option>
              </select>
              <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>This affects the tone and focus of AI generated descriptions</div>
            </div>

            <div className="form-group">
              <label>Default Price Display for New Vehicles</label>
              <select value={aiSettings.default_price_display} onChange={e => setAiSettings(a => ({ ...a, default_price_display: e.target.value }))}>
                <option value="full_price">Show Full Price</option>
                <option value="payment_only">Payment Only (Down + Weekly)</option>
                <option value="payment_biweekly">Payment Only (Down + Bi-Weekly)</option>
                <option value="payment_monthly">Payment Only (Down + Monthly)</option>
                <option value="call_for_price">Call for Price</option>
              </select>
              <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>New vehicles will default to this display mode</div>
            </div>

            <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#e0e0e0' }}>Include Pricing in AI Descriptions</div>
                  <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>When enabled, AI descriptions mention specific down payment and weekly payment amounts</div>
                </div>
                <div onClick={() => setAiSettings(a => ({ ...a, ai_include_pricing: !a.ai_include_pricing }))}
                  style={{ width: 44, height: 24, borderRadius: 12, background: aiSettings.ai_include_pricing ? '#cc0000' : '#333', cursor: 'pointer', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
                  <div style={{ width: 18, height: 18, borderRadius: 9, background: '#fff', position: 'absolute', top: 3, left: aiSettings.ai_include_pricing ? 23 : 3, transition: 'left .2s' }} />
                </div>
              </div>
            </div>

            <button className="btn-red" onClick={saveAI} disabled={saving}>{saving ? 'Saving...' : 'Save AI Settings'}</button>
          </div>
        </div>
      )}

      {tab === 'password' && (
        <div style={{ maxWidth: 400 }}>
          <div className="form-section">
            <h3>Change Password</h3>
            <div className="form-group">
              <label>Current Password</label>
              <input type="password" value={passwords.current} onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))} placeholder="Enter current password" />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={passwords.newPass} onChange={e => setPasswords(p => ({ ...p, newPass: e.target.value }))} placeholder="At least 8 characters" />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input type="password" value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} placeholder="Repeat new password" />
            </div>
            <button className="btn-red" onClick={changePassword} disabled={savingPassword}>{savingPassword ? 'Changing...' : 'Change Password'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
